import { styled, GetProps, XStack, YStack, Text } from 'tamagui'
import type { ReactNode } from 'react'

const SelectorFrame = styled(XStack, {
  name: 'QuantitySelector',
  alignItems: 'center',
  gap: '$2',
})

const SelectorButton = styled(YStack, {
  name: 'QuantitySelectorButton',
  tag: 'button',
  
  width: 44,
  height: 44,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$backgroundMuted',
  borderWidth: 2,
  borderColor: '$borderColor',
  borderRadius: 0,
  cursor: 'pointer',
  
  pressStyle: {
    backgroundColor: '$borderColor',
    scale: 0.95,
  },
  
  hoverStyle: {
    borderColor: '$colorMuted',
  },
  
  disabledStyle: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
})

const SelectorButtonText = styled(Text, {
  name: 'QuantitySelectorButtonText',
  fontSize: 20,
  fontWeight: '700',
  color: '$color',
})

const SelectorDisplay = styled(YStack, {
  name: 'QuantitySelectorDisplay',
  alignItems: 'center',
  minWidth: 60,
})

const SelectorValue = styled(Text, {
  name: 'QuantitySelectorValue',
  fontSize: 24,
  fontWeight: '800',
  fontFamily: '$mono',
  color: '$color',
})

const SelectorUnit = styled(Text, {
  name: 'QuantitySelectorUnit',
  fontSize: 10,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: '$colorMuted',
})

export interface QuantitySelectorProps extends GetProps<typeof SelectorFrame> {
  value: number
  unit?: string
  min?: number
  max?: number
  onChange: (value: number) => void
  renderValue?: (value: number) => ReactNode
}

export function QuantitySelector({
  value,
  unit,
  min = 1,
  max = 999,
  onChange,
  renderValue,
  ...props
}: QuantitySelectorProps) {
  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1)
    }
  }

  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1)
    }
  }

  return (
    <SelectorFrame {...props}>
      <SelectorButton
        onPress={handleDecrement}
        disabled={value <= min}
        aria-label="Decrease quantity"
      >
        <SelectorButtonText>−</SelectorButtonText>
      </SelectorButton>
      
      <SelectorDisplay>
        <SelectorValue>
          {renderValue ? renderValue(value) : value}
        </SelectorValue>
        {unit && <SelectorUnit>{unit}</SelectorUnit>}
      </SelectorDisplay>
      
      <SelectorButton
        onPress={handleIncrement}
        disabled={value >= max}
        aria-label="Increase quantity"
      >
        <SelectorButtonText>+</SelectorButtonText>
      </SelectorButton>
    </SelectorFrame>
  )
}

QuantitySelector.displayName = 'QuantitySelector'
